'use client';
import { EmailVerification } from '@/components/email-verification';
import { GoBack } from '@/components/go-back';
import OptimizedLogo from '@/components/optimized-logo';
import { SignUpForm } from '@/components/sign-up-form';
import { SocialAuth } from '@/components/social-auth';
import { buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

export default function Signup() {
  const navigation = useRouter();

  const [isWaitingForVerification, setIsWaitingForVerification] =
    useState(false);

  const [userEmail, setUserEmail] = useState<string>('');

  const onRegisterSuccess = useCallback((email: string) => {
    setUserEmail(email);
    setIsWaitingForVerification(true);
  }, []);

  return (
    <Card className="w-[400px] rounded-2xl relative">
      {!isWaitingForVerification && (
        <GoBack className="absolute top-5 left-4" />
      )}

      <CardHeader>
        <CardTitle className="flex items-center justify-center flex-col gap-6">
          <Link href="/">
            <OptimizedLogo color="black" width={200} height={32} />
          </Link>

          <h2 className="font-bold text-2xl">
            {isWaitingForVerification ? 'Verify account' : 'Sign up'}
          </h2>
        </CardTitle>
      </CardHeader>
      {isWaitingForVerification ? (
        <EmailVerification
          userEmail={userEmail}
          onBack={() => navigation.back()}
        />
      ) : (
        <CardContent className="space-y-4">
          <SignUpForm onRegisterSuccess={onRegisterSuccess} />
          <p className="text-center text-sm text-[#24292E]">OR</p>
          <SocialAuth />

          <p className="text-center text-sm text-[#24292E]">
            Already have an account?
            <Link
              className={cn(buttonVariants({ variant: 'link' }), 'p-1')}
              href="sign-in"
            >
              Log in
            </Link>
          </p>
        </CardContent>
      )}

      {!isWaitingForVerification && (
        <CardFooter>
          <p className="text-center text-[10px] text-[#24292E]">
            By continuing, you agree to our
            <Link
              className={cn(
                buttonVariants({ variant: 'link' }),
                'p-0 text-[10px] h-fit'
              )}
              href="/terms-of-service"
            >
              Terms of Service
            </Link>{' '}
            and acknowledge that you understand the{' '}
            <Link
              className={cn(
                buttonVariants({ variant: 'link' }),
                'p-0 text-[10px] h-fit'
              )}
              href="/privacy-policy"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </CardFooter>
      )}
    </Card>
  );
}
